// react
import { Suspense, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useNavigate } from '@tanstack/react-router';

// material ui
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from '@mui/material';

// api
import { useGetAccountsQuery } from '@/api/recalc/endpoints/accountsApiSlice';
import { useGetBuildingsQuery } from '@/api/recalc/endpoints/buildingApiSlice';
import { useGetSubscriptionsQuery } from '@/api/recalc/endpoints/jobSubscriptionApiSlice';

// shared components
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import OperationsCard from '@/features/building-optimization/components/operations/OperationsCard';
import type { Account } from '@/features/building-optimization/components/operations/OperationsDataGrid';

// components
import FilterPanel from '@/features/operations/predictive-heat-control/FilterPanel';
import accountGroups from '@/utils/accountGroups.json';
import enrichAccounts from '@/utils/enrichAccounts';

export const Route = createFileRoute(
  '/$accountId/_admin/operations/predictive-heat-control/groups/',
)({
  component: RouteComponent,
  staticData: { crumb: 'glossary:predictive-heat-control' },
});

declare module '@tanstack/react-router' {
  interface HistoryState {
    accountGroup?: Account[];
    activeFilter?: boolean;
  }
}

const groupAccountsById = (
  accounts: Account[],
  groups: Record<string, number[]>,
): Record<string, Account[]> => {
  const grouped: Record<string, Account[]> = {};

  // Initialize groups with empty arrays
  for (const group in groups) {
    if (Object.prototype.hasOwnProperty.call(groups, group)) {
      grouped[group] = [];
    }
  }
  grouped['Others'] = [];

  accounts?.forEach((account) => {
    let assigned = false;
    for (const [group, ids] of Object.entries(groups)) {
      if (ids.includes(account.AccountId)) {
        grouped[group].push(account);
        assigned = true;
        break;
      }
    }

    if (!assigned) {
      grouped['Others'].push(account);
    }
  });

  return grouped;
};

function RouteComponent() {
  const { t } = useTranslation('operations');
  const [activeFilter, setActiveFilter] = useState(true);

  const navigate = useNavigate();

  const handleCardDoubleClick = (groupKey: string, accountGroup: Account[]) => {
    const encodedGroupKey = encodeURIComponent(groupKey);
    navigate({
      to: `${encodedGroupKey}`,
      state: { accountGroup, activeFilter },
    });
  };

  const { data: buildings, isLoading: isLoadingBuildings } =
    useGetBuildingsQuery({
      pollingInterval: 3000,
      skipPollingIfUnfocused: true,
    });
  const { data: accounts, isLoading: isLoadingAccounts } = useGetAccountsQuery({
    pollingInterval: 3000,
    skipPollingIfUnfocused: true,
  });
  const { data: subscriptions, isLoading: isLoadingSubscriptions } =
    useGetSubscriptionsQuery({
      pollingInterval: 3000,
      skipPollingIfUnfocused: true,
    });

  // Determine the current loading state
  const loadingMessage = useMemo(() => {
    if (isLoadingAccounts) return t('loadingAccounts');
    if (isLoadingBuildings) return t('loadingBuildings');
    if (isLoadingSubscriptions) return t('loadingSubscriptions');
    return null;
  }, [isLoadingAccounts, isLoadingBuildings, isLoadingSubscriptions, t]);

  // Memoize enriched accounts
  const enrichedAccountsMemo = useMemo(() => {
    if (!loadingMessage) {
      return enrichAccounts(accounts, buildings, subscriptions);
    }
    return [];
  }, [loadingMessage, accounts, buildings, subscriptions]);

  const groupedAccounts = groupAccountsById(
    enrichedAccountsMemo,
    accountGroups,
  );

  const filteredAccountGroups = useMemo(() => {
    return Object.entries(groupedAccounts).reduce<Record<string, Account[]>>(
      (result, [groupKey, groupAccounts]) => {
        const filteredAccounts = groupAccounts
          .map((account) => {
            const filteredBuildings = account.Buildings.filter((building) =>
              building.JobSubscriptions.some(
                (sub) => !activeFilter || sub.StateCode === 'Active',
              ),
            );
            return { ...account, Buildings: filteredBuildings };
          })
          .filter((account) => account.Buildings.length > 0);

        if (filteredAccounts.length > 0) {
          result[groupKey] = filteredAccounts;
        }
        return result;
      },
      {} as Record<string, Account[]>,
    );
  }, [groupedAccounts, activeFilter]);

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <PageHeader title="Operations / Predictive Heat Control" disableArrow />

      <FilterPanel
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
      />
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 4 }}>
              {t('gettingReady')}
            </Typography>
          </Box>
        }
      >
        {loadingMessage ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '70vh',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 4 }}>
              {loadingMessage}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {Object.entries(filteredAccountGroups)?.map(
              ([groupKey, accounts]) => (
                <Grid
                  key={groupKey}
                  size={{
                    sm: 12,
                    md: 6,
                    lg: 6,
                    xl: 4
                  }}>
                  <OperationsCard
                    groupKey={groupKey}
                    accounts={accounts}
                    onCardDoubleClick={() =>
                      handleCardDoubleClick(groupKey, accounts)
                    }
                    activeFilter={activeFilter}
                  />
                </Grid>
              ),
            )}
          </Grid>
        )}
      </Suspense>
    </Container>
  );
}
